import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useSalesforce } from '../hooks/useSalesforce';
import { useWorkspace } from '../context/WorkspaceContext';
import { useSubscription } from '../hooks/useSubscription';
import { useToast } from '../context/ToastContext';
import { useExportEligibility } from '../hooks/useExportEligibility';
import Navbar from '../components/Navbar';
import Tooltip from '../components/Tooltip';
import { supabase } from '../services/supabaseClient';
import { getFlowById, getAuthToken } from '../services/salesforceService';
import { getSummaryStatus, SummaryStatus } from '../services/claudeService';
import { exportPageToPDF, printPage } from '../utils/pdfExportUtils';

// Import our new component-based structure
import FlowHeader from '../components/FlowHeader';
import FlowMetadata from '../components/FlowMetadata';
import FlowSummary from '../components/FlowSummary';
import FlowFieldReferences from '../components/FlowFieldReferences';
import FlowElementsDisplay from '../components/FlowElementsDisplay';
import WorkspaceAnalysisSection from '../components/WorkspaceAnalysisSection';
import { ReauthModal } from '../components/ReauthModal';
import PerformanceScorecardInline from '../components/PerformanceScorecardInline';
import { getFlowSummary } from '../utils/flowUtils';

/**
 * Flow Detail Page
 * Shows detailed information about a specific Salesforce flow.
 */
export default function FlowDetailPage() {
  const { flowId, orgId } = useParams<{ flowId: string; orgId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refreshFlowSummary } = useSalesforce();
  const { workspaces, currentWorkspace, setCurrentWorkspace, setCurrentOrgId } = useWorkspace();
  const { subscription } = useSubscription();
  const { showToast } = useToast();
  const { checkEligibility, recordExport } = useExportEligibility();

  // State for flow data and loading status
  const [flow, setFlow] = useState<any>(null);
  const [orgData, setOrgData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [refreshingSummary, setRefreshingSummary] = useState<boolean>(false);
  const [refreshingSummaryMap, setRefreshingSummaryMap] = useState<Record<string, boolean>>({});
  const [summaryStatus, setSummaryStatus] = useState<SummaryStatus | null>(null);
  const [showReauthModal, setShowReauthModal] = useState<boolean>(false);
  const [reauthOrgName, setReauthOrgName] = useState<string>('');
  const [showExportDropdown, setShowExportDropdown] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const exportDropdownRef = useRef<HTMLDivElement>(null);
  
  // Set current org in workspace context
  useEffect(() => {
    if (orgId) {
      setCurrentOrgId(orgId);
    }
  }, [orgId, setCurrentOrgId]);

  // Close export dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target as Node)) {
        setShowExportDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Fetch org data for building Salesforce URLs
  useEffect(() => {
    if (!orgId) return;
    
    const fetchOrgData = async () => {
      try {
        const { data, error } = await supabase
          .from('sf_orgs')
          .select('instance_url')
          .eq('id', orgId)
          .single();
          
        if (error) {
          console.error('Error fetching org data:', error);
        } else {
          setOrgData(data);
        }
      } catch (error) {
        console.error('Error fetching org data:', error);
      }
    };
    
    fetchOrgData();
  }, [orgId]);

  // Initialize with the specified flow ID for this page
  useEffect(() => {
    if (!flowId || !orgId) {
      setError(new Error('Flow ID or Organization ID is missing'));
      setLoading(false);
      return;
    }
    
    loadFlowDetails(false);
  }, [flowId, orgId]);
  
  // Function to load flow details from API
  const loadFlowDetails = async (forceRefresh = false) => {
    console.log('loadFlowDetails called with:', { flowId, orgId, forceRefresh });
    
    if (!flowId || !orgId) {
      setError(new Error('Flow ID or Organization ID is missing'));
      setLoading(false);
      return;
    }
    
    // Only show loading spinner on initial load, not refresh
    if (!flow) {
      setLoading(true);
    }
    setError(null);
    
    try {
      console.log(`Loading flow details for flow ${flowId} in org ${orgId}`);
      const flowData = await getFlowById(orgId, flowId, forceRefresh);
      
      if (!flowData) {
        throw new Error('No data returned from API');
      }
      
      console.log('Flow data received:', {
        hasMetadata: !!flowData.metadata,
        fromDatabase: !!flowData.fromDatabase,
        hasAiSummary: 'ai_summary' in flowData
      });
      
      // Set the flow data state
      setFlow(flowData);
      
      // Check summary status if needed
      if (!flowData.ai_summary) {
        checkSummaryStatus();
      }
    } catch (error) {
      console.error('Error loading flow details:', error);
      
      // Check if it's an authentication error
      const errorMessage = error instanceof Error ? error.message : String(error);
      const isAuthError = errorMessage.includes('INVALID_SESSION') || 
                         errorMessage.includes('authentication') ||
                         errorMessage.includes('expired') ||
                         (error as any)?.requiresReauth;
                         
      if (isAuthError) {
        // Get org name for the modal
        try {
          const { data: org } = await supabase
            .from('sf_orgs')
            .select('name')
            .eq('id', orgId)
            .single();
          
          if (org) {
            setReauthOrgName(org.name);
            setShowReauthModal(true);
          }
        } catch (orgError) {
          console.error('Error fetching org name:', orgError);
          // Show modal with generic name if we can't get the org name
          setReauthOrgName('your Salesforce org');
          setShowReauthModal(true);
        }
      } else {
        setError(error instanceof Error ? error : new Error(String(error)));
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  // Function to handle refreshing flow data from Salesforce
  const handleRefreshFlow = async () => {
    if (refreshing) return;

    setRefreshing(true);
    try {
      // First check if there are any updates
      const checkResponse = await fetch('/api/salesforce/flows/check-updates-enhanced', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          orgId,
          access_token: (await getAuthToken(orgId)).access_token,
          instance_url: (await getAuthToken(orgId)).instance_url
        })
      });

      if (!checkResponse.ok) {
        throw new Error('Failed to check for updates');
      }

      const checkResult = await checkResponse.json();

      // If no updates, show message and return
      if (!checkResult.has_updates) {
        setRefreshing(false);
        showToast('Flow is up to date!', 'info');
        return;
      }

      // Updates found, proceed with sync
      const response = await fetch('/api/salesforce/flows/sync-single-flow', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify({
          flowId,
          orgId
        })
      });

      if (!response.ok) {
        let errorMessage = 'Failed to sync flow';
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const error = await response.json();
            errorMessage = error.error || error.message || errorMessage;
          } else {
            const text = await response.text();
            errorMessage = text || `Server returned status ${response.status}`;
          }
        } catch (parseError) {
          errorMessage = `Server returned status ${response.status}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('Flow synced from Salesforce:', result);
      
      // Poll for processing completion
      let attempts = 0;
      const maxAttempts = 20; // 20 attempts * 3 seconds = 60 seconds max
      
      const checkForCompletion = async () => {
        if (attempts >= maxAttempts) {
          setRefreshing(false);
          // Reload one more time to get whatever was processed
          await loadFlowDetails(false);
          showToast('Flow refresh timed out, but data has been partially updated.', 'info');
          return;
        }
        
        attempts++;
        
        // Check processing status
        const { data: flowStatus } = await supabase
          .from('sf_flow_definitions')
          .select('processing_status, active_version_id, last_modified_date')
          .eq('id', flowId)
          .single();
        
        if (!flowStatus) {
          setRefreshing(false);
          return;
        }
        
        // Check if processing is complete
        if (flowStatus.processing_status === 'completed' ||
            flowStatus.processing_status === 'ready' ||
            flowStatus.processing_status === 'scoring_complete') {
          // Processing complete, reload the flow data
          console.log('Flow processing complete, reloading data...');
          await loadFlowDetails(false);
          setRefreshing(false);
          showToast('Flow refreshed successfully from Salesforce!', 'success');
        } else {
          // Still processing, check again
          console.log(`Flow processing status: ${flowStatus.processing_status}, checking again in 3s...`);
          setTimeout(checkForCompletion, 3000);
        }
      };

      // Start checking after initial delay
      setTimeout(checkForCompletion, 2000);

    } catch (error) {
      console.error('Error during refresh:', error);
      setRefreshing(false);
      showToast(`Failed to refresh flow: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
    }
  };

  const handleExportMarkdown = async () => {
    setShowExportDropdown(false);
    setIsExporting(true);
    
    try {
      if (!flow) return;
      
      // Get full flow details including summaries and elements
      const { data: flowDetails, error } = await supabase
        .from('sf_flow_definitions')
        .select(`
          *,
          sf_flow_summaries(*),
          sf_flow_elements(*),
          sf_orgs(name)
        `)
        .eq('id', flow.id)
        .single();
        
      if (error) throw error;
      
      // Build markdown content
      let markdownContent = `# ${flowDetails.label || flowDetails.api_name}\n\n`;
      markdownContent += `**Organization:** ${flowDetails.sf_orgs?.name || 'Unknown'}\n`;
      markdownContent += `**Export Date:** ${new Date().toLocaleString()}\n\n`;
      markdownContent += `---\n\n`;
      
      // Basic Information
      markdownContent += `## Basic Information\n\n`;
      markdownContent += `**API Name:** ${flowDetails.api_name}\n`;
      markdownContent += `**Type:** ${flowDetails.process_type}\n`;
      markdownContent += `**Performance Grade:** ${flowDetails.performance_grade || 'Unscored'}\n`;
      if (flowDetails.description) {
        markdownContent += `**Description:** ${flowDetails.description}\n`;
      }
      markdownContent += `\n`;
      
      // Add summaries for each workspace
      if (flowDetails.sf_flow_summaries && flowDetails.sf_flow_summaries.length > 0) {
        markdownContent += `## AI Analysis\n\n`;
        flowDetails.sf_flow_summaries.forEach((summary: any) => {
          const workspace = workspaces.find(w => w.id === summary.workspace_id);
          if (workspace && summary.content) {
            markdownContent += `### ${workspace.name}\n\n`;
            markdownContent += `${summary.content}\n\n`;
          }
        });
      }
      
      // Add trigger information
      if (flowDetails.trigger_info) {
        markdownContent += `## Trigger Information\n\n`;
        if (flowDetails.trigger_info.triggerType) {
          markdownContent += `**Trigger Type:** ${flowDetails.trigger_info.triggerType}\n`;
        }
        if (flowDetails.trigger_info.objectType) {
          markdownContent += `**Object:** ${flowDetails.trigger_info.objectType}\n`;
        }
        if (flowDetails.trigger_info.recordTriggerType) {
          markdownContent += `**When:** ${flowDetails.trigger_info.recordTriggerType}\n`;
        }
        markdownContent += `\n`;
      }
      
      // Add flow elements
      if (flowDetails.sf_flow_elements && flowDetails.sf_flow_elements.length > 0) {
        const elementTypes = flowDetails.sf_flow_elements.reduce((acc: any, element: any) => {
          acc[element.element_type] = (acc[element.element_type] || 0) + 1;
          return acc;
        }, {});
        
        markdownContent += `## Flow Elements (${flowDetails.sf_flow_elements.length} total)\n\n`;
        Object.entries(elementTypes).forEach(([type, count]) => {
          markdownContent += `- ${type}: ${count}\n`;
        });
        markdownContent += `\n`;
      }
      
      // Add metadata
      if (flowDetails.metadata) {
        markdownContent += `## Additional Details\n\n`;
        if (flowDetails.metadata.activeVersionNumber) {
          markdownContent += `**Version:** ${flowDetails.metadata.activeVersionNumber}\n`;
        }
        if (flowDetails.metadata.lastModifiedDate) {
          markdownContent += `**Last Modified:** ${new Date(flowDetails.metadata.lastModifiedDate).toLocaleDateString()}\n`;
        }
        markdownContent += `\n`;
      }
      
      // Download the file
      const blob = new Blob([markdownContent], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${flowDetails.api_name}-${new Date().toISOString().split('T')[0]}.md`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Export error:', error);
      showToast('Failed to export flow. Please try again.', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportPDF = async () => {
    setShowExportDropdown(false);
    setIsExporting(true);
    
    try {
      if (!flow) return;
      
      console.log('PDF Export - Current flow data:', flow);
      console.log('PDF Export - Available workspaces:', workspaces);
      
      // Get full flow details including summaries and elements
      const { data: flowDetails, error } = await supabase
        .from('sf_flow_definitions')
        .select(`
          *,
          sf_flow_summaries(*),
          sf_flow_elements(*),
          sf_orgs(name)
        `)
        .eq('id', flow.id)
        .single();
        
      if (error) {
        console.error('PDF Export - Supabase error:', error);
        throw error;
      }
      
      console.log('PDF Export - Fetched flow details:', flowDetails);
      
      // Performance data is already in the flow object from the main page
      const performanceData = flow.performance_scorecard || null;
      
      // Create a new window with formatted content for PDF
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        throw new Error('Please allow popups to export PDF');
      }
      
      // Helper function to get grade color
      const getGradeColor = (grade: string) => {
        switch(grade) {
          case 'A': return '#059669'; // green-600
          case 'B': return '#10B981'; // green-500
          case 'C': return '#EAB308'; // yellow-500
          case 'D': return '#F97316'; // orange-500
          case 'F': case 'I': return '#EF4444'; // red-500
          default: return '#6B7280'; // gray-500
        }
      };
      
      // Helper function to get risk level color
      const getRiskColor = (level: string) => {
        switch(level?.toUpperCase()) {
          case 'LOW': case 'PASS': return '#059669'; // green-600
          case 'MEDIUM': case 'WARNING': return '#D97706'; // yellow-600
          case 'HIGH': case 'FAIL': return '#DC2626'; // red-600
          default: return '#6B7280'; // gray-500
        }
      };
      
      // Build HTML content with enhanced styling matching FlowDocs design
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${flowDetails.master_label || flowDetails.label || flow.master_label || flow.label || flowDetails.api_name || flow.api_name || 'Flow'} - FlowDocs Export</title>
          <style>
            @page {
              size: letter;
              margin: 0.75in;
            }
            
            @media print {
              body { margin: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              .page-break { page-break-after: always; }
              .no-print { display: none !important; }
            }
            
            * {
              box-sizing: border-box;
            }
            
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
              line-height: 1.6;
              color: #1a202c;
              margin: 0;
              padding: 20px;
              background: white;
            }
            
            /* Header Styling */
            .header {
              border-bottom: 3px solid #3182ce;
              padding-bottom: 20px;
              margin-bottom: 30px;
            }
            
            .header h1 {
              margin: 0 0 10px 0;
              color: #1a202c;
              font-size: 28px;
              font-weight: 700;
            }
            
            .header .subtitle {
              color: #4a5568;
              font-size: 14px;
              margin: 5px 0;
            }
            
            .flowdocs-brand {
              font-weight: 700;
              color: #3182ce;
              font-size: 16px;
              margin-bottom: 10px;
            }
            
            /* Badge Styling */
            .badges {
              display: flex;
              gap: 10px;
              flex-wrap: wrap;
              margin-top: 10px;
            }
            
            .badge {
              display: inline-block;
              padding: 4px 12px;
              font-size: 12px;
              font-weight: 600;
              border-radius: 9999px;
              text-transform: uppercase;
            }
            
            .badge-blue {
              background-color: #dbeafe;
              color: #1e40af;
            }
            
            .badge-green {
              background-color: #d1fae5;
              color: #065f46;
            }
            
            .badge-yellow {
              background-color: #fef3c7;
              color: #92400e;
            }
            
            .badge-grade {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              width: 60px;
              height: 60px;
              border-radius: 50%;
              font-size: 24px;
              font-weight: 700;
              color: white;
              margin-right: 15px;
            }
            
            /* Section Styling */
            h2 {
              color: #2d3748;
              font-size: 20px;
              font-weight: 600;
              margin: 30px 0 15px 0;
              padding-bottom: 8px;
              border-bottom: 2px solid #e2e8f0;
            }
            
            h3 {
              color: #4a5568;
              font-size: 16px;
              font-weight: 600;
              margin: 20px 0 10px 0;
            }
            
            p {
              margin: 0 0 12px 0;
              line-height: 1.7;
            }
            
            /* Info Box Styling */
            .info-box {
              background: #f7fafc;
              border: 1px solid #e2e8f0;
              border-radius: 8px;
              padding: 16px;
              margin: 16px 0;
            }
            
            .info-box p {
              margin: 4px 0;
            }
            
            /* Grid Layouts */
            .metrics-grid {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 15px;
              margin: 20px 0;
            }
            
            .metric-card {
              background: #f7fafc;
              border: 1px solid #e2e8f0;
              border-radius: 8px;
              padding: 15px;
            }
            
            .metric-card .label {
              font-size: 12px;
              color: #718096;
              text-transform: uppercase;
              margin-bottom: 5px;
            }
            
            .metric-card .value {
              font-size: 18px;
              font-weight: 600;
            }
            
            /* Table Styling */
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 16px 0;
              font-size: 14px;
            }
            
            th {
              background: #f7fafc;
              color: #4a5568;
              font-weight: 600;
              text-align: left;
              padding: 12px;
              border-bottom: 2px solid #e2e8f0;
            }
            
            td {
              padding: 12px;
              border-bottom: 1px solid #e2e8f0;
            }
            
            /* List Styling */
            ul {
              margin: 12px 0;
              padding-left: 20px;
            }
            
            li {
              margin: 6px 0;
              color: #4a5568;
            }
            
            /* Performance Section */
            .performance-header {
              display: flex;
              align-items: center;
              margin: 20px 0;
            }
            
            .risk-indicators {
              display: flex;
              gap: 15px;
              margin: 20px 0;
            }
            
            .risk-card {
              flex: 1;
              background: white;
              border: 2px solid;
              border-radius: 8px;
              padding: 12px;
              text-align: center;
            }
            
            .risk-card .label {
              font-size: 11px;
              color: #718096;
              text-transform: uppercase;
              margin-bottom: 4px;
            }
            
            .risk-card .value {
              font-size: 16px;
              font-weight: 700;
            }
            
            /* Issue List */
            .issue-item {
              background: #fff5f5;
              border: 1px solid #fed7d7;
              border-radius: 6px;
              padding: 12px;
              margin: 8px 0;
            }
            
            .issue-item.warning {
              background: #fffbeb;
              border-color: #fde68a;
            }
            
            .issue-severity {
              display: inline-block;
              font-size: 11px;
              font-weight: 700;
              text-transform: uppercase;
              margin-right: 8px;
            }
            
            /* Markdown Content */
            .markdown-content {
              line-height: 1.8;
              color: #2d3748;
            }
            
            .markdown-content h1, .markdown-content h2, .markdown-content h3 {
              margin-top: 24px;
              margin-bottom: 12px;
            }
            
            .markdown-content strong {
              color: #1a202c;
              font-weight: 600;
            }
            
            .markdown-content code {
              background: #edf2f7;
              padding: 2px 6px;
              border-radius: 4px;
              font-family: 'Menlo', 'Monaco', 'Courier New', monospace;
              font-size: 13px;
            }
            
            .markdown-content pre {
              background: #f7fafc;
              border: 1px solid #e2e8f0;
              border-radius: 6px;
              padding: 16px;
              overflow-x: auto;
            }
            
            /* Section Headers */
            h4 {
              color: #2d3748;
              font-size: 14px;
              font-weight: 600;
              margin: 20px 0 12px 0;
              padding-top: 16px;
              border-top: 1px solid #e2e8f0;
            }
            
            h4:first-child {
              border-top: none;
              padding-top: 0;
            }
            
            /* Footer */
            .footer {
              margin-top: 50px;
              padding-top: 20px;
              border-top: 2px solid #e2e8f0;
              text-align: center;
              color: #718096;
              font-size: 12px;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="flowdocs-brand">FlowDocs</div>
            <h1>${flowDetails.master_label || flowDetails.label || flow.master_label || flow.label || flowDetails.api_name || flow.api_name || 'Flow'}</h1>
            <div class="subtitle">
              <strong>Organization:</strong> ${flowDetails.sf_orgs?.name || 'Unknown'} | 
              <strong>Export Date:</strong> ${new Date().toLocaleString()}
            </div>
            <div class="badges">
              ${flowDetails.namespace_prefix ? `<span class="badge badge-blue">${flowDetails.namespace_prefix}</span>` : ''}
              <span class="badge badge-blue">${flowDetails.process_type || flow.process_type || 'Unknown Type'}</span>
            </div>
          </div>
          
          ${flowDetails.description ? `
            <section>
              <h2>Description</h2>
              <p>${flowDetails.description}</p>
            </section>
          ` : ''}
          
          ${flowDetails.sf_flow_summaries && flowDetails.sf_flow_summaries.length > 0 ? `
            <section>
              <h2>Flow Analysis</h2>
              ${flowDetails.sf_flow_summaries.map(summary => {
                const workspace = workspaces.find(w => w.id === summary.workspace_id);
                // Check for ai_summary in different possible locations
                const summaryContent = summary.ai_summary || summary.content || summary.summary;
                if (!workspace || !summaryContent) return '';
                
                // Convert markdown-style formatting to HTML
                let content = summaryContent
                  .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                  .replace(/\n/g, '<br>');
                
                return `
                  <div class="info-box">
                    <h3>${workspace.name}${workspace.is_default ? ' (Default)' : ''}</h3>
                    <div class="markdown-content">${content}</div>
                  </div>
                `;
              }).join('')}
            </section>
          ` : ''}
          
          ${flowDetails.performance_grade || performanceData ? `
            <section>
              <h2>Performance Analysis</h2>
              <div class="performance-header">
                ${flowDetails.performance_grade ? `
                  <div class="badge-grade" style="background-color: ${getGradeColor(flowDetails.performance_grade)}">
                    ${flowDetails.performance_grade}
                  </div>
                ` : ''}
                <div>
                  <h3 style="margin: 0;">Overall Performance Score</h3>
                  ${flowDetails.performance_score !== null ? `
                    <p style="margin: 4px 0; font-size: 24px; font-weight: 600;">${flowDetails.performance_score}/100</p>
                  ` : ''}
                  ${flowDetails.performance_summary ? `
                    <p style="margin: 4px 0; color: #718096;">${flowDetails.performance_summary}</p>
                  ` : ''}
                </div>
              </div>
              
              ${performanceData || flowDetails.performance_issues ? `
                <div class="risk-indicators">
                  ${performanceData ? `
                    <div class="risk-card" style="border-color: ${getRiskColor(performanceData.query_performance?.risk_level)};">
                      <div class="label">Query Performance</div>
                      <div class="value" style="color: ${getRiskColor(performanceData.query_performance?.risk_level)};">
                        ${performanceData.query_performance?.risk_level || 'Unknown'}
                      </div>
                    </div>
                    <div class="risk-card" style="border-color: ${getRiskColor(performanceData.dml_performance?.risk_level)};">
                      <div class="label">DML Performance</div>
                      <div class="value" style="color: ${getRiskColor(performanceData.dml_performance?.risk_level)};">
                        ${performanceData.dml_performance?.risk_level || 'Unknown'}
                      </div>
                    </div>
                    <div class="risk-card" style="border-color: ${getRiskColor(performanceData.bulk_safety?.risk_level)};">
                      <div class="label">Bulk Safety</div>
                      <div class="value" style="color: ${getRiskColor(performanceData.bulk_safety?.risk_level)};">
                        ${performanceData.bulk_safety?.risk_level || 'Unknown'}
                      </div>
                    </div>
                    <div class="risk-card" style="border-color: ${getRiskColor(performanceData.cpu_performance?.risk_level)};">
                      <div class="label">CPU Performance</div>
                      <div class="value" style="color: ${getRiskColor(performanceData.cpu_performance?.risk_level)};">
                        ${performanceData.cpu_performance?.risk_level || 'Unknown'}
                      </div>
                    </div>
                  ` : `
                    <div class="info-box" style="width: 100%;">
                      <p>Detailed performance metrics not available. Run performance analysis to see risk indicators.</p>
                    </div>
                  `}
                </div>
              ` : ''}
              
              ${flowDetails.performance_scorecard || flow.performance_scorecard ? (() => {
                const scorecard = flowDetails.performance_scorecard || flow.performance_scorecard;
                return `
                  <h3>Performance Details</h3>
                  
                  <h4>Performance Metrics</h4>
                  <div class="metrics-grid">
                    <div class="metric-card">
                      <div class="label">Total Queries</div>
                      <div class="value">${scorecard.details?.totalQueries || 0}</div>
                    </div>
                    <div class="metric-card">
                      <div class="label">Queries in Loops</div>
                      <div class="value" style="color: ${scorecard.details?.queriesInLoops > 0 ? '#DC2626' : 'inherit'};">
                        ${scorecard.details?.queriesInLoops || 0}
                      </div>
                    </div>
                    <div class="metric-card">
                      <div class="label">Total DML Operations</div>
                      <div class="value">${scorecard.details?.totalDmlOperations || 0}</div>
                    </div>
                    <div class="metric-card">
                      <div class="label">DML in Loops</div>
                      <div class="value" style="color: ${scorecard.details?.dmlInLoops > 0 ? '#DC2626' : 'inherit'};">
                        ${scorecard.details?.dmlInLoops || 0}
                      </div>
                    </div>
                    <div class="metric-card">
                      <div class="label">Error Handling</div>
                      <div class="value" style="color: ${scorecard.details?.hasErrorHandling ? '#059669' : '#DC2626'};">
                        ${scorecard.details?.hasErrorHandling ? 'Yes' : 'No'}
                      </div>
                    </div>
                    <div class="metric-card">
                      <div class="label">Bulkified</div>
                      <div class="value" style="color: ${scorecard.details?.isBulkified ? '#059669' : '#D97706'};">
                        ${scorecard.details?.isBulkified ? 'Yes' : 'No'}
                      </div>
                    </div>
                    <div class="metric-card">
                      <div class="label">Element Count</div>
                      <div class="value">${scorecard.details?.elementCount || 0}</div>
                    </div>
                    <div class="metric-card">
                      <div class="label">Loops Count</div>
                      <div class="value">${scorecard.details?.loops || 0}</div>
                    </div>
                  </div>
                  
                  ${scorecard.issues && scorecard.issues.length > 0 ? `
                    <h4>Detected Issues</h4>
                    ${scorecard.issues.map(issue => `
                      <div class="issue-item ${issue.severity === 'SHOWSTOPPER' || issue.severity === 'CRITICAL' ? '' : 'warning'}">
                        <span class="issue-severity" style="color: ${
                          issue.severity === 'SHOWSTOPPER' ? '#6B46C1' :
                          issue.severity === 'CRITICAL' ? '#DC2626' :
                          issue.severity === 'MAJOR' ? '#EA580C' :
                          issue.severity === 'MODERATE' ? '#D97706' :
                          '#CA8A04'
                        };">${issue.severity || 'ISSUE'}</span>
                        <strong>${issue.type || issue.detectorName || 'Issue'}</strong>
                        ${issue.description ? `: ${issue.description}` : ''}
                        ${issue.impact ? `<br><em>Impact: ${issue.impact}</em>` : ''}
                      </div>
                    `).join('')}
                  ` : ''}
                  
                  ${scorecard.recommendations && scorecard.recommendations.length > 0 ? `
                    <h4>Recommendations</h4>
                    ${scorecard.recommendations.map(rec => `
                      <div class="info-box">
                        <strong>${rec.title}</strong>
                        <span style="float: right; font-size: 11px; color: ${
                          rec.impact === 'HIGH' ? '#DC2626' :
                          rec.impact === 'MEDIUM' ? '#D97706' :
                          '#059669'
                        };">${rec.impact} IMPACT</span>
                        <p style="margin-top: 8px;">${rec.description}</p>
                      </div>
                    `).join('')}
                  ` : ''}
                  
                  ${scorecard.grading ? `
                    <h4>Grading Breakdown</h4>
                    <table>
                      <thead>
                        <tr>
                          <th>Category</th>
                          <th>Points</th>
                          <th>Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>Base Score</strong></td>
                          <td><strong>100</strong></td>
                          <td>Starting score for all flows</td>
                        </tr>
                        ${scorecard.grading?.deductions?.map(d => `
                          <tr>
                            <td>${d.category}</td>
                            <td style="color: #DC2626;">-${d.points}</td>
                            <td>${d.reason}</td>
                          </tr>
                        `).join('') || ''}
                        <tr style="border-top: 2px solid #e2e8f0;">
                          <td><strong>Final Score</strong></td>
                          <td><strong>${scorecard.overall_score || 0}</strong></td>
                          <td><strong>Grade: ${scorecard.overall_grade || 'N/A'}</strong></td>
                        </tr>
                      </tbody>
                    </table>
                  ` : ''}
                `;
              })() : ''}
              
              ${flowDetails.performance_issues ? (() => {
                let issues = flowDetails.performance_issues;
                if (typeof issues === 'string') {
                  try {
                    issues = JSON.parse(issues);
                  } catch (e) {
                    return '';
                  }
                }
                
                const allIssues = [...(issues.showstoppers || []), ...(issues.deductions || [])];
                if (allIssues.length === 0) return '';
                
                return `
                  <h3>Detected Issues</h3>
                  ${allIssues.map(issue => `
                    <div class="issue-item ${issue.severity === 'SHOWSTOPPER' || issue.severity === 'CRITICAL' ? '' : 'warning'}">
                      <span class="issue-severity" style="color: ${
                        issue.severity === 'SHOWSTOPPER' ? '#6B46C1' :
                        issue.severity === 'CRITICAL' ? '#DC2626' :
                        issue.severity === 'MAJOR' ? '#EA580C' :
                        issue.severity === 'MODERATE' ? '#D97706' :
                        '#CA8A04'
                      };">${issue.severity || 'ISSUE'}</span>
                      <strong>${issue.detector || issue.name || 'Issue'}</strong>
                      ${issue.message ? `: ${issue.message}` : ''}
                    </div>
                  `).join('')}
                `;
              })() : ''}
            </section>
          ` : ''}
          
          ${flowDetails.trigger_info || flow.trigger_info ? (() => {
            const triggerInfo = flowDetails.trigger_info || flow.trigger_info;
            return `
              <section>
                <h2>Trigger Information</h2>
                <div class="info-box">
                  ${triggerInfo.triggerType ? `<p><strong>Trigger Type:</strong> ${triggerInfo.triggerType}</p>` : ''}
                  ${triggerInfo.objectType ? `<p><strong>Object:</strong> ${triggerInfo.objectType}</p>` : ''}
                  ${triggerInfo.recordTriggerType ? `<p><strong>When:</strong> ${triggerInfo.recordTriggerType}</p>` : ''}
                  ${triggerInfo.conditionsDescription ? `<p><strong>Conditions:</strong> ${triggerInfo.conditionsDescription}</p>` : ''}
                </div>
              </section>
            `;
          })() : ''}
          
          ${flowDetails.sf_flow_elements && flowDetails.sf_flow_elements.length > 0 ? (() => {
            // Group elements by type
            const elementsByType = flowDetails.sf_flow_elements.reduce((acc, element) => {
              const type = element.element_type || 'Unknown';
              if (!acc[type]) acc[type] = [];
              acc[type].push(element);
              return acc;
            }, {});
            
            return `
              <section>
                <h2>Flow Elements (${flowDetails.sf_flow_elements.length} total)</h2>
                ${Object.entries(elementsByType).map(([type, elements]) => `
                  <div class="info-box">
                    <h3>${type} (${elements.length})</h3>
                    <ul>
                      ${elements.slice(0, 10).map(el => `
                        <li>${el.name || el.element_name || 'Unnamed'}${el.label && el.label !== el.name ? ` (${el.label})` : ''}</li>
                      `).join('')}
                      ${elements.length > 10 ? `<li><em>... and ${elements.length - 10} more</em></li>` : ''}
                    </ul>
                  </div>
                `).join('')}
              </section>
            `;
          })() : ''}
          
          ${flow.variables && Object.keys(flow.variables).length > 0 ? `
            <section>
              <h2>Flow Variables & Resources</h2>
              <div class="info-box">
                ${Object.entries(flow.variables).map(([key, vars]) => {
                  if (!Array.isArray(vars) || vars.length === 0) return '';
                  const typeLabel = key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1');
                  return `
                    <h3>${typeLabel} (${vars.length})</h3>
                    <ul>
                      ${vars.map(v => `<li>${v.name}${v.dataType ? ` - ${v.dataType}` : ''}</li>`).join('')}
                    </ul>
                  `;
                }).join('')}
              </div>
            </section>
          ` : ''}
          
          ${(flow.field_references && flow.field_references.length > 0) || (flowDetails.field_references && flowDetails.field_references.length > 0) ? (() => {
            // Use field references from either source
            const fieldRefs = flow.field_references || flowDetails.field_references || [];
            
            // Group field references by object
            const fieldsByObject = fieldRefs.reduce((acc, ref) => {
              const obj = ref.objectType || 'Unknown';
              if (!acc[obj]) acc[obj] = [];
              acc[obj].push(ref);
              return acc;
            }, {});
            
            return `
              <section>
                <h2>Fields Used in Flow</h2>
                <table>
                  <thead>
                    <tr>
                      <th>Object</th>
                      <th>Field</th>
                      <th>Type</th>
                      <th>Used In</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${Object.entries(fieldsByObject).map(([objName, fields]) => 
                      fields.map((field, index) => `
                        <tr>
                          ${index === 0 ? `<td rowspan="${fields.length}" style="vertical-align: top; font-weight: 600;">${objName}</td>` : ''}
                          <td>${field.fieldName || 'Unknown'}</td>
                          <td>${field.relationship || '-'}</td>
                          <td>${field.elementName || '-'}</td>
                        </tr>
                      `).join('')
                    ).join('')}
                  </tbody>
                </table>
              </section>
            `;
          })() : ''}
          
          <section>
            <h2>Flow Metadata</h2>
            <div class="metrics-grid">
              <div class="metric-card">
                <div class="label">API Name</div>
                <div class="value">${flowDetails.api_name || flow.api_name || 'N/A'}</div>
              </div>
              <div class="metric-card">
                <div class="label">Version</div>
                <div class="value">${flowDetails.metadata?.activeVersionNumber || flowDetails.version_number || flow.metadata?.activeVersionNumber || flow.version_number || 'N/A'}</div>
              </div>
              <div class="metric-card">
                <div class="label">Last Modified</div>
                <div class="value">${(flowDetails.last_modified_date || flow.last_modified_date) ? new Date(flowDetails.last_modified_date || flow.last_modified_date).toLocaleDateString() : 'N/A'}</div>
              </div>
              <div class="metric-card">
                <div class="label">Modified By</div>
                <div class="value">${flowDetails.last_modified_by || flow.last_modified_by || flowDetails.metadata?.LastModifiedBy?.Name || flow.metadata?.LastModifiedBy?.Name || 'Unknown'}</div>
              </div>
            </div>
          </section>
          
          <div class="footer">
            <p>Generated by FlowDocs &bull; ${new Date().toLocaleDateString()} &bull; Page 1 of 1</p>
          </div>
        </body>
        </html>
      `;
      
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      
      // Wait for content to load then trigger print
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          // Close the window after a delay to ensure print dialog has opened
          setTimeout(() => {
            printWindow.close();
          }, 1000);
        }, 500);
      };
      
    } catch (error) {
      console.error('PDF export error:', error);
      // More specific error message
      if (error instanceof Error) {
        showToast(`Failed to export PDF: ${error.message}`, 'error');
      } else {
        showToast('Failed to export PDF. Please try again.', 'error');
      }
    } finally {
      setIsExporting(false);
    }
  };
  
  // Function to handle regenerating the flow summary for a specific workspace
  const handleRegenerateSummary = async (workspaceId: string) => {
    if (!flowId || !orgId) return;
    
    const workspace = workspaces.find(w => w.id === workspaceId);
    if (!workspace) {
      console.error('Workspace not found:', workspaceId);
      return;
    }
    
    setRefreshingSummaryMap(prev => ({ ...prev, [workspaceId]: true }));
    
    try {
      console.log('Regenerating flow analysis for workspace:', workspace.name);
      await refreshFlowSummary(orgId, flowId, workspaceId);
      
      // Poll for completion
      let attempts = 0;
      const maxAttempts = 30; // 30 seconds max
      
      const pollForCompletion = setInterval(async () => {
        attempts++;
        
        try {
          // Check if summary is complete by looking at the flow's processing status
          const { data: flowStatus } = await supabase
            .from('sf_flow_definitions')
            .select('processing_status, summary_status')
            .eq('id', flowId)
            .single();
          
          if (!flowStatus || flowStatus.processing_status === 'completed' || flowStatus.processing_status === 'ready' || flowStatus.summary_status === 'completed') {
            clearInterval(pollForCompletion);
            
            // Reload the flow data
            console.log('Summary generation complete, reloading flow data...');
            
            // Add a small delay to ensure the summary is saved
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Fetch the updated flow data directly
            try {
              const { data: updatedFlow, error: fetchError } = await supabase
                .from('sf_flow_definitions')
                .select(`
                  *,
                  sf_flow_summaries(
                    id,
                    workspace_id,
                    ai_summary,
                    structured_analysis,
                    step_by_step_analysis,
                    workspaces(
                      id,
                      name,
                      is_active,
                      is_default
                    )
                  )
                `)
                .eq('id', flowId)
                .eq('org_id', orgId)
                .single();
                
              if (!fetchError && updatedFlow) {
                console.log('Successfully fetched updated flow data');
                
                // Fetch flow elements
                const { data: flowElements } = await supabase
                  .from('sf_flow_elements')
                  .select('*')
                  .eq('flow_definition_id', updatedFlow.id);
                
                // Format the flow data similar to getFlowById
                const formattedFlow = {
                  ...updatedFlow,
                  flow_elements: { elements: flowElements || [] },
                  field_references: [],
                  // Ensure sf_flow_summaries is always an array
                  sf_flow_summaries: Array.isArray(updatedFlow.sf_flow_summaries) 
                    ? updatedFlow.sf_flow_summaries 
                    : [],
                  fromDatabase: true
                };
                
                // Update the flow state directly
                setFlow(formattedFlow);
              } else {
                console.error('Error fetching updated flow:', fetchError);
                // Fallback to loadFlowDetails
                await loadFlowDetails(false);
              }
            } catch (err) {
              console.error('Error in direct fetch:', err);
              await loadFlowDetails(false);
            }
            
            // Force component update
            setRefreshingSummaryMap(prev => ({ ...prev, [workspaceId]: false }));
            showToast(`Flow analysis regenerated successfully for ${workspace.name}!`, 'success');
          } else if (attempts >= maxAttempts) {
            clearInterval(pollForCompletion);
            console.log('Summary generation timed out, reloading anyway...');
            await loadFlowDetails(false);
            setRefreshingSummaryMap(prev => ({ ...prev, [workspaceId]: false }));
            showToast('Analysis generation timed out, but data has been partially updated.', 'info');
          }
        } catch (error) {
          console.error('Error checking summary status:', error);
          if (attempts >= maxAttempts) {
            clearInterval(pollForCompletion);
            setRefreshingSummaryMap(prev => ({ ...prev, [workspaceId]: false }));
          }
        }
      }, 1000); // Check every second
      
    } catch (error) {
      console.error('Error refreshing analysis:', error);
      setRefreshingSummaryMap(prev => ({ ...prev, [workspaceId]: false }));
      showToast(`Failed to regenerate analysis: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
    }
  };
  
  // Function to check summary generation status
  const checkSummaryStatus = async () => {
    if (!flowId || !orgId) return;
    
    try {
      console.log('Checking summary status');
      const status = await getSummaryStatus(orgId, flowId);
      
      if (status) {
        setSummaryStatus(status);
        
        // If summary is complete, load the flow details again to get the updated summary
        if (status.status === 'completed') {
          console.log('Summary generation completed, reloading flow details');
          await loadFlowDetails(false);
          setRefreshingSummary(false);
        } else if (status.status === 'in_progress' || status.status === 'pending') {
          // If still in progress, check again in a few seconds
          console.log('Summary generation in progress, checking again in 5 seconds');
          setTimeout(() => checkSummaryStatus(), 5000);
        } else {
          // If failed or unknown, stop refreshing
          console.log('Summary generation status:', status.status);
          setRefreshingSummary(false);
        }
      } else {
        // If no status, assume it's not being generated
        setRefreshingSummary(false);
      }
    } catch (error) {
      console.error('Error checking summary status:', error);
      setRefreshingSummary(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar />
      
      <main className="py-10">
        <div className="max-w-7xl mx-auto sm:px-6 lg:px-8">
          {/* Breadcrumb section */}
          <div className="px-4 sm:px-0 mb-4">
            <div className="flex justify-between items-center">
              <nav className="flex" aria-label="Breadcrumb">
                <ol className="flex items-center space-x-4">
                  <li>
                    <div>
                      <a 
                        href="#" 
                        onClick={(e) => { e.preventDefault(); navigate('/orgs'); }} 
                        className="text-gray-400 hover:text-gray-500"
                      >
                        Organizations
                      </a>
                    </div>
                  </li>
                  <li>
                    <div className="flex items-center">
                      <svg className="flex-shrink-0 h-5 w-5 text-gray-300" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path d="M5.555 17.776l8-16 .894.448-8 16-.894-.448z" />
                      </svg>
                      <a 
                        href="#"
                        onClick={(e) => { e.preventDefault(); navigate(`/${orgId}/flows`); }} 
                        className="ml-4 text-gray-400 hover:text-gray-500"
                      >
                        Flows
                      </a>
                    </div>
                  </li>
                  <li>
                    <div className="flex items-center">
                      <svg className="flex-shrink-0 h-5 w-5 text-gray-300" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path d="M5.555 17.776l8-16 .894.448-8 16-.894-.448z" />
                      </svg>
                      <span className="ml-4 text-gray-500 font-medium">
                        {flow?.master_label || flow?.MasterLabel || flow?.metadata?.MasterLabel || flow?.name || 'Flow Details'}
                      </span>
                    </div>
                  </li>
                </ol>
              </nav>
              
              {/* Action buttons */}
              <div className="flex space-x-3">
                {/* Refresh button */}
                <Tooltip content="Refresh this flow's data from Salesforce">
                  <button
                    type="button"
                    onClick={handleRefreshFlow}
                    disabled={refreshing}
                    className={`inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm ${
                      refreshing
                        ? 'bg-gray-400 text-white cursor-not-allowed'
                        : 'text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                    }`}
                  >
                  {refreshing ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Checking for Updates...
                    </>
                  ) : (
                    <>
                      <svg className="-ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Check for Updates to this Flow
                    </>
                  )}
                  </button>
                </Tooltip>

                {/* Export Dropdown */}
                <div className="relative" ref={exportDropdownRef}>
                  <button
                    type="button"
                    onClick={async () => {
                      // Starter users: no export access
                      if (!subscription.can_export && !subscription.is_trial) {
                        showToast('Export functionality is available with Professional or Enterprise plans. Please upgrade to export flows.', 'info');
                        navigate('/plans');
                        return;
                      }

                      // Trial users: check daily limit
                      if (subscription.is_trial) {
                        const eligibility = await checkEligibility();
                        if (!eligibility.can_export) {
                          showToast("You've already exported a flow today. Trial users can export one flow per day. Upgrade for unlimited exports.", 'info');
                          return;
                        }
                      }

                      // Show export dropdown
                      setShowExportDropdown(!showExportDropdown);
                    }}
                    disabled={isExporting || !flow}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    {isExporting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Exporting...
                      </>
                    ) : (
                      <>
                        <svg className="-ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                        </svg>
                        Export this Flow
                        <svg className="ml-2 -mr-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </>
                    )}
                  </button>
                  
                  {showExportDropdown && (
                    <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                      <div className="py-1" role="menu" aria-orientation="vertical">
                        <button
                          onClick={async () => {
                            await handleExportMarkdown();
                            // Record export for trial users
                            if (subscription.is_trial) {
                              try {
                                await recordExport();
                                console.log('[EXPORT] Recorded markdown export for trial user');
                              } catch (error) {
                                console.error('[EXPORT] Failed to record export:', error);
                              }
                            }
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          role="menuitem"
                        >
                          Export as Markdown
                        </button>
                        <button
                          onClick={async () => {
                            setShowExportDropdown(false);
                            setIsExporting(true);
                            try {
                              const flowName = flow?.master_label || flow?.label || flow?.api_name || 'flow';
                              await exportPageToPDF('flow-detail-content', {
                                filename: flowName.replace(/[^a-z0-9]/gi, '-').toLowerCase(),
                                includeDate: true
                              });

                              // Record export for trial users
                              if (subscription.is_trial) {
                                try {
                                  await recordExport();
                                  showToast('Flow exported successfully!', 'success');
                                  console.log('[EXPORT] Recorded PDF export for trial user');
                                } catch (error) {
                                  console.error('[EXPORT] Failed to record export:', error);
                                  showToast('Flow exported, but failed to record export limit. Please contact support if this persists.', 'error');
                                }
                              }
                            } catch (error) {
                              console.error('PDF export error:', error);
                              showToast('Failed to export PDF. Please try again.', 'error');
                            } finally {
                              setIsExporting(false);
                            }
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          role="menuitem"
                        >
                          Export as PDF
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          
          {/* Main content area */}
          <div id="flow-detail-content" className="bg-white rounded-lg shadow px-5 py-6 sm:px-6">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p className="ml-3 text-lg text-gray-600">Loading flow details...</p>
              </div>
            ) : error ? (
              <div className={`${error.message.includes('Please click "Refresh"') ? 'bg-yellow-50' : 'bg-red-50'} p-4 rounded-md`}>
                <div className="flex">
                  <div className="flex-shrink-0">
                    {error.message.includes('Please click "Refresh"') ? (
                      <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <div className="ml-3">
                    <h3 className={`text-sm font-medium ${error.message.includes('Please click "Refresh"') ? 'text-yellow-800' : 'text-red-800'}`}>
                      {error.message.includes('Please click "Refresh"') ? 'Flow Data Required' : 'Error loading flow details'}
                    </h3>
                    <div className={`mt-2 text-sm ${error.message.includes('Please click "Refresh"') ? 'text-yellow-700' : 'text-red-700'}`}>
                      <p>{error.message}</p>
                    </div>
                    <div className="mt-4 flex space-x-3">
                      {error.message.includes('Please click "Refresh"') && (
                        <Tooltip content="Refresh this flow's data from Salesforce">
                          <button
                            onClick={handleRefreshFlow}
                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            Check for Updates to this Flow
                          </button>
                        </Tooltip>
                      )}
                      <button
                        onClick={() => navigate(`/${orgId}/flows`)}
                        className={`inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md ${
                          error.message.includes('Please click "Refresh"') 
                            ? 'text-yellow-700 bg-yellow-100 hover:bg-yellow-200 focus:ring-yellow-500' 
                            : 'text-red-700 bg-red-100 hover:bg-red-200 focus:ring-red-500'
                        } focus:outline-none focus:ring-2 focus:ring-offset-2`}
                      >
                        Return to Flows
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : flow ? (
              <div className="space-y-6">
                {/* Flow Header Component */}
                <FlowHeader
                  flow={flow}
                  orgData={orgData}
                />

                {/* Performance Scorecard - moved to top for prominence */}
                {flowId && flow && (
                  <PerformanceScorecardInline
                    flowId={flowId}
                    flowName={flow.master_label || flow.MasterLabel || flow.name || 'Flow'}
                  />
                )}

                {/* Flow Summary Section (formerly General Documentation) - Only show default workspace */}
                {workspaces
                  .filter(w => w.org_id === orgId && w.is_active && w.is_default)
                  .map((workspace, index) => {
                    return (
                      <WorkspaceAnalysisSection
                        key={workspace.id}
                        workspace={workspace}
                        flow={flow}
                        refreshingSummary={refreshingSummaryMap[workspace.id] || false}
                        onRegenerateSummary={handleRegenerateSummary}
                        index={index}
                      />
                    );
                  })}
                
                {/* Flow Elements and Variables Component */}
                <FlowElementsDisplay flow={flow} />
                
                {/* Field References Component */}
                <FlowFieldReferences flow={flow} />
                
                {/* Flow Metadata Component - moved to bottom */}
                <FlowMetadata flow={flow} orgData={orgData} />
              </div>
            ) : (
              <div className="py-8 text-center">
                <p className="text-gray-500">No flow data available.</p>
              </div>
            )}
          </div>
        </div>
      </main>
      
      {/* Re-authentication Modal */}
      {orgId && (
        <ReauthModal
          isOpen={showReauthModal}
          onClose={() => setShowReauthModal(false)}
          orgId={orgId}
          orgName={reauthOrgName}
        />
      )}
      
    </div>
  );
}